import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { ArrowRight, BarChart3, Target, Shield, TrendingUp, Clock } from "lucide-react";
import { motion } from "framer-motion";
import SalesChatBot from "../components/SalesChatBot";
import { usePageSEO } from "../lib/seo";

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  usePageSEO({ title: null, description: "Turn AI into your #1 revenue engine. We expose where you're losing money, where AI creates immediate upside, and exactly what to deploy next.", path: "/" });

  const handleStartAudit = () => {
    navigate("/booking");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="landing-page">
      {/* Header */}
      <header className="backdrop-blur-2xl bg-white/80 border-b border-slate-200/60 sticky top-0 z-50" data-testid="landing-header" role="banner">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 min-w-0">
            <img src="/stansbury-logo.png" alt="Stansbury AI" className="h-[37px] sm:h-12 flex-shrink-0 w-auto" />
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {user ? (
              <Button onClick={() => navigate("/dashboard")} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none px-6 font-['Outfit']" data-testid="landing-dashboard-btn">Dashboard</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate("/auth")} className="hidden sm:inline-flex text-slate-600 hover:text-slate-900 font-['Outfit']" data-testid="landing-login-btn">Sign In</Button>
                <Button onClick={handleStartAudit} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none px-4 sm:px-6 text-sm sm:text-base font-['Outfit']" data-testid="landing-start-audit-btn">Book a Strategy Call</Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <main>
      <section className="relative overflow-hidden" data-testid="hero-section" aria-label="Stansbury AI Growth Blueprint">
        <div className="absolute inset-0 z-0">
          <img src="https://images.pexels.com/photos/1029622/pexels-photo-1029622.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/85 backdrop-blur-sm" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.6 }} className="max-w-3xl">
            <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-6">AI Growth Infrastructure</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Outfit'] font-medium tracking-tighter text-[#0F172A] mb-6 leading-[1.1]">
              Turn AI Into Your<br />#1 Revenue Engine
            </h1>
            <p className="text-base md:text-lg text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-10 max-w-2xl">
              We map your operational infrastructure, quantify revenue leaks, and architect the AI systems that eliminate them — before your competitors build theirs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={handleStartAudit} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none px-8 py-3 h-12 text-base font-['Outfit'] font-medium" data-testid="hero-start-audit-btn">
                Book a Strategy Call <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })} className="border-slate-300 text-slate-800 hover:bg-slate-50 rounded-none px-8 py-3 h-12 text-base font-['Outfit']" data-testid="hero-learn-more-btn">
                See How It Works
              </Button>
            </div>
          </motion.div>
          {/* Score preview card */}
          <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="hidden lg:block absolute right-24 top-1/2 -translate-y-1/2 w-72">
            <div className="bg-white border border-slate-200 p-6 shadow-lg">
              <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-slate-500 mb-4">Sample Score</p>
              <div className="text-6xl font-['Outfit'] font-light text-[#0033CC] mb-2">67</div>
              <p className="text-sm text-slate-500 mb-4">/100 — Operationalizing</p>
              <div className="space-y-2">
                {[{ label: "Acquire", value: 72 }, { label: "Convert", value: 58 }, { label: "Operate", value: 45 }, { label: "Expand", value: 80 }, { label: "Defend", value: 62 }].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <span className="text-xs text-slate-500 w-16 font-['IBM_Plex_Sans']">{item.label}</span>
                    <div className="flex-1 h-1.5 bg-slate-100">
                      <div className="h-full bg-[#0033CC] transition-all" style={{ width: `${item.value}%` }} />
                    </div>
                    <span className="text-xs font-['JetBrains_Mono'] text-slate-600 w-8 text-right">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trusted By */}
      <section className="py-12 bg-white border-t border-slate-200/60" data-testid="trusted-by-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-slate-400 text-center mb-8">Trusted by</p>
          <div className="flex flex-wrap items-center justify-center gap-10 md:gap-14">
            {[
              { src: "/trust-quantus.png", alt: "Quantus" },
              { src: "/trust-lendio.png", alt: "Lendio", className: "h-[31px]" },
              { src: "/trust-cody.png", alt: "Cody" },
              { src: "/trust-inkfree.png", alt: "Ink Free", className: "h-10", style: { filter: "grayscale(100%) brightness(0.55)" } },
              { src: "/trust-cleardefense.png", alt: "ClearDefense AI" },
            ].map((logo) => (
              <img key={logo.alt} src={logo.src} alt={logo.alt} className={logo.className || "h-9"} style={logo.style || { filter: "grayscale(100%) brightness(0.8)" }} />
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-24 md:py-32 bg-white border-t border-slate-200" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-4">Process</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-16">How it works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "01", icon: Target, title: "Executive Discovery", desc: "Complete a guided operational assessment across 12 business dimensions. Takes about 15 minutes." },
              { step: "02", icon: BarChart3, title: "Infrastructure Analysis", desc: "Receive your AI readiness score, revenue leak analysis, and operational transformation map." },
              { step: "03", icon: TrendingUp, title: "Transformation Blueprint", desc: "Access your full executive blueprint with AI architecture, implementation roadmap, and ROI projections." }
            ].map((item, i) => (
              <motion.div key={item.step} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }} className="border border-slate-200 p-8 bg-white hover:border-slate-300 transition-colors">
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-xs font-['JetBrains_Mono'] text-slate-400">{item.step}</span>
                  <div className="w-10 h-10 bg-[#E5EDFF] flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-[#0033CC]" strokeWidth={1.5} />
                  </div>
                </div>
                <h3 className="text-xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-3">{item.title}</h3>
                <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-24 md:py-32 bg-[#FAFAFA] border-t border-slate-200" data-testid="what-you-get-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-4">Deliverables</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-16">What your blueprint includes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: BarChart3, title: "Revenue Leak Analysis", desc: "Quantified breakdown of where revenue is escaping your business — and how much." },
              { icon: Target, title: "AI Opportunity Matrix", desc: "Prioritized transformation opportunities ranked by ROI, speed, and difficulty." },
              { icon: Clock, title: "Implementation Roadmap", desc: "Phased execution plan with timelines, dependencies, and complexity scoring." },
              { icon: Shield, title: "Transformation Blueprint", desc: "Executive-grade report with department plans, AI architecture, and ROI projections." }
            ].map((item, i) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white border border-slate-200 p-6 hover:border-slate-300 transition-colors">
                <item.icon className="w-5 h-5 text-[#0033CC] mb-4" strokeWidth={1.5} />
                <h4 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mb-2">{item.title}</h4>
                <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-24 md:py-32 bg-[#FAFAFA] border-t border-slate-200" data-testid="case-study-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Left - Content */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-white p-10 md:p-14 flex flex-col justify-center">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-6 leading-[1.1]">Bold steps forward.</h2>
              <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-3">Featured Client Success Story</p>
              <div className="w-full h-px bg-slate-200 mb-6" />
              <h3 className="text-xl sm:text-2xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-6 leading-tight">
                An EMD Transforms Itself<br />Amid Intense Volatility
              </h3>
              <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-4">The Impact</p>
              <div className="flex gap-4 mb-8">
                <div className="bg-[#F1F5F9] p-5 flex-1">
                  <div className="text-2xl sm:text-3xl font-['Outfit'] font-medium text-[#0F172A] mb-1">34%</div>
                  <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed">revenue increase within first 120 days</p>
                </div>
                <div className="bg-[#F1F5F9] p-5 flex-1">
                  <div className="text-2xl sm:text-3xl font-['Outfit'] font-medium text-[#0F172A] mb-1">3.2x</div>
                  <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed">ROI on AI implementation investment</p>
                </div>
              </div>
              <Button onClick={() => navigate("/client-success/an-emd-transforms-itself-amid-intense-volatility")} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none px-8 py-3 h-11 font-['Outfit'] font-medium" data-testid="case-study-link">
                Read Story <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
            {/* Right - Image */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative min-h-[300px] lg:min-h-0">
              <img src="https://images.pexels.com/photos/16585160/pexels-photo-16585160.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Business strategy meeting" className="w-full h-full object-cover absolute inset-0" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 md:py-32 bg-white border-t border-slate-200" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-4">Get Started</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-4">Invest in clarity. Build with confidence.</h2>
            <p className="text-base text-slate-500 font-['IBM_Plex_Sans'] max-w-xl mx-auto">Every blueprint creates implementation inevitability. Our clients typically see 20-40% operational improvement in their first 90 days.</p>
          </div>
          <div className="max-w-2xl mx-auto">
            <div className="border-2 border-[#0033CC] bg-[#0033CC]/[0.02] p-10 md:p-12" data-testid="pricing-card">
              <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-4">AI Growth Infrastructure Blueprint</p>
              <h3 className="text-3xl font-['Outfit'] font-medium text-[#0F172A] mb-3">Transformation Blueprint</h3>
              <p className="text-base text-slate-500 font-['IBM_Plex_Sans'] mb-8 max-w-lg">A white-glove strategic engagement. We map your operations, quantify your revenue leaks, and architect the AI infrastructure to eliminate them.</p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  "Revenue Leak Analysis with dollar estimates",
                  "AI Opportunity Matrix — ranked by ROI",
                  "12-dimension operational assessment",
                  "Department-level transformation plans",
                  "Custom AI infrastructure architecture",
                  "Implementation roadmap with phased execution",
                  "ROI modeling with payback projections",
                  "AI readiness scoring with blockers identified",
                  "System architecture diagrams",
                  "Executive-grade PDF report",
                  "Implementation sprint planning",
                  "Strategy call with senior advisor",
                ].map((f) => (
                  <div key={f} className="text-sm text-slate-600 font-['IBM_Plex_Sans'] flex items-start gap-2">
                    <span className="text-[#0033CC] mt-0.5 font-bold">+</span> {f}
                  </div>
                ))}
              </div>
              <Button onClick={() => navigate("/booking")} className="w-full bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-12 text-base font-['Outfit'] font-medium" data-testid="pricing-contact-btn">
                Book a Strategy Call <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ - SEO */}
      <section className="py-24 md:py-32 bg-[#FAFAFA] border-t border-slate-200" data-testid="faq-section" aria-label="Frequently Asked Questions">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-4">FAQ</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-16">Common questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
            {[
              { q: "What is the Transformation Blueprint?", a: "A comprehensive operational assessment across 12 business dimensions — from sales and marketing to operations, automation, and AI readiness. It quantifies your revenue leaks, maps AI transformation opportunities, and delivers an executive-grade blueprint with department-level plans and ROI projections." },
              { q: "How does the process work?", a: "It starts with a strategy call where we learn about your business. Then you complete a guided executive discovery (about 15 minutes). Our AI engine analyzes your responses across 12 dimensions and generates your full transformation blueprint with financial modeling and implementation roadmap." },
              { q: "What do I receive?", a: "A Revenue Leak Analysis with dollar estimates, AI Opportunity Matrix ranked by ROI, department-by-department transformation plans, implementation roadmap with dependencies, ROI modeling, AI readiness scoring, and a downloadable executive PDF report." },
              { q: "Is my data confidential?", a: "Absolutely. All data is encrypted and handled with strict confidentiality. Your blueprint results are only accessible to you and our team, and are never shared with third parties." },
            ].map((item, i) => (
              <div key={`idx-${i}`} className="border-t border-slate-200 pt-6">
                <h3 className="text-base font-['Outfit'] font-medium text-[#0F172A] mb-3">{item.q}</h3>
                <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0F172A] text-white" data-testid="cta-section" aria-label="Call to Action">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-slate-500 mb-6">Ready to start?</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-['Outfit'] font-medium tracking-tight mb-6">
            Stop guessing. Start growing with AI.
          </h2>
          <p className="text-base text-slate-400 font-['IBM_Plex_Sans'] mb-10 max-w-xl mx-auto">
            Join hundreds of businesses that have used the Stansbury AI blueprint to identify their highest-impact opportunities.
          </p>
          <Button onClick={handleStartAudit} className="bg-white text-[#0F172A] hover:bg-slate-100 rounded-none px-10 py-3 h-12 text-base font-['Outfit'] font-medium" data-testid="cta-start-audit-btn">
            Book a Strategy Call <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-12 border-t border-slate-800" data-testid="landing-footer" role="contentinfo">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <img src="/stansbury-logo-white.png" alt="Stansbury AI" className="h-10" />
          </div>
          <div className="flex items-center gap-4">
            <a href="/privacy" className="text-xs text-slate-500 hover:text-slate-300 font-['IBM_Plex_Sans'] transition-colors">Privacy Policy</a>
            <span className="text-slate-700">|</span>
            <a href="/insights" className="text-xs text-slate-500 hover:text-slate-300 font-['IBM_Plex_Sans'] transition-colors">Insights</a>
            <span className="text-slate-700">|</span>
            <a href="/contact" className="text-xs text-slate-500 hover:text-slate-300 font-['IBM_Plex_Sans'] transition-colors">Contact</a>
            <span className="text-slate-700">|</span>
            <p className="text-xs text-slate-500 font-['IBM_Plex_Sans']">Confidential & Secure</p>
          </div>
        </div>
      </footer>
      <SalesChatBot />
    </div>
  );
}
